import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link as RouterLink, useNavigate, useSearchParams } from "react-router-dom";
import {
  AccountTreeOutlined,
  AltRouteOutlined,
  BuildOutlined,
  CheckCircleOutline,
  PlayArrowOutlined,
  PublishOutlined,
  SaveOutlined,
  Search,
} from "@mui/icons-material";
import {
  Alert,
  Box,
  Breadcrumbs,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  Link,
  Menu,
  MenuItem,
  Skeleton,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import PageBand from "../../../layouts/page-detail/PageBand";
import WorkflowCanvasDialog from "../../../features/workflows/WorkflowCanvasDialog";
import WorkflowCanvasIcon from "../../../features/workflows/WorkflowCanvasIcon";
import WorkflowCanvasPalette from "../../../features/workflows/WorkflowCanvasPalette";
import WorkflowFlow from "../../../features/workflows/WorkflowFlow";
import WorkflowSettingsDialog from "../../../features/workflows/WorkflowSettingsDialog";
import WorkflowValidationDialog from "../../../features/workflows/WorkflowValidationDialog";
import {
  createStarterWorkflowCanvases,
  createWorkflowCanvas,
  WORKFLOW_CANVAS_LABELS,
  WorkflowCanvas,
  WorkflowCanvasType,
} from "../../../features/workflows/workflowBuilderData";
import {
  applySaveResult,
  WorkflowBuilderGraph,
} from "../../../features/workflows/workflowGraphMapper";
import {
  NEW_WORKFLOW_CATEGORY,
  useWorkflowBuilderGraph,
  WORKFLOW_SAVE_MESSAGES,
  WorkflowSettingsValues,
} from "../../../features/workflows/useWorkflowBuilderGraph";
import {
  allRowsPassed,
  mergeServerIssues,
  runClientValidation,
  issuesByCanvas,
  withServerIssues,
  WorkflowInlineIssues,
  WORKFLOW_VALIDATION_MESSAGES,
  WorkflowValidationRow,
} from "../../../features/workflows/workflowValidation";
import { WorkflowReadOnlyProvider } from "../../../features/workflows/WorkflowReadOnlyContext";
import { useMenu } from "../../../context/MenuContext";
import ConfirmationDialog from "../../../components/ui/ConfirmationDialog";
import { useUnsavedChangesGuard } from "../../../hooks/useUnsavedChangesGuard";
import { useWorkflowBuilderTrace } from "../../../features/workflows/useWorkflowBuilderTrace";
import { ModuleNames, SEVERITIES } from "../../../data/constants";
import { useToaster } from "../../../hooks/useToaster";
import { WorkflowValidationIssueDto } from "../../../types/workflowDefinitionDto";
import { getWorkflowOpenTestRuns } from "../../../services/workflowService";
import "./WorkflowBuilder.scss";

const BREADCRUMB_LINK_COLOR = "rgba(255, 255, 255, 0.75)";

// A Builder opened without a definition is the "New workflow" case: an unsaved draft that starts
// from the Form + Finalize skeleton rather than someone else's flow.
const NEW_WORKFLOW = {
  name: "Untitled workflow",
  category: NEW_WORKFLOW_CATEGORY,
  version: 1,
  status: "draft" as const,
};

const LOAD_ERROR_MESSAGE = "This workflow couldn't be loaded.";

const LAST_CANVAS_MESSAGE = "A workflow needs at least one canvas";
const REORDERED_MESSAGE = "Canvas reordered";

const UNSAVED_CHANGES_MESSAGE = "You have unsaved changes. Leave without saving?";

// Epic 80431 (Pooja Pujara, 2026-10-19): asked before a new test run when the tester has open ones.
export const UNFINISHED_TEST_RUNS_TITLE = "Unfinished test runs";
export const unfinishedTestRunsMessage = (count: number) =>
  `You have ${count} unfinished test ${count === 1 ? "run" : "runs"} of this workflow. Discard ${count === 1 ? "it" : "them"}?`;

const testRunUrl = (definitionId: number) => `/workflow/run?definitionId=${definitionId}&source=builder`;

const READ_ONLY_MESSAGE = "You can view this workflow, but you don't have permission to edit it.";

const WORKFLOW_SETTINGS_MESSAGES = {
  SAVED: "Workflow settings saved",
  ARCHIVED: "Workflow archived",
  UNARCHIVED: "Workflow un-archived",
  EDITS_NOT_CARRIED: "The new version was created, but your unsaved changes couldn't be copied into it.",
} as const;

const nonDraftSaveMessage = (status: string) =>
  `This version is ${status}. Use Save as new version in workflow settings to keep these changes.`;

const WorkflowBuilder: React.FC = () => {
  const navigate = useNavigate();
  const showToaster = useToaster();
  const [searchParams] = useSearchParams();
  const definitionId = Number(searchParams.get("definitionId"));

  // A new workflow starts on the Form + Finalize skeleton; a loaded one replaces it below. The
  // canvases are edited in place, so the graph is copied into state once rather than re-read.
  const [workflowTitle, setWorkflowTitle] = useState(NEW_WORKFLOW.name);
  const [canvases, setCanvases] = useState<WorkflowCanvas[]>(createStarterWorkflowCanvases);
  const [selectedId, setSelectedId] = useState(canvases[0]?.id ?? "");
  const [editingCanvasId, setEditingCanvasId] = useState<string | null>(null);
  const [canvasSnapshot, setCanvasSnapshot] = useState<WorkflowCanvas | null>(null);
  const [armedDeleteId, setArmedDeleteId] = useState<string | null>(null);
  // An armed "Confirm?" disarms after a few seconds or on any click that isn't a delete button.
  // One timer per arming, cleared on re-arm, so arming a second tile isn't cut short by the first.
  useEffect(() => {
    if (!armedDeleteId) return undefined;
    const timer = window.setTimeout(() => setArmedDeleteId(null), 2600);
    const handleMouseDown = (event: MouseEvent) => {
      if (event.target instanceof Element && event.target.closest("[data-delete-arm]")) return;
      setArmedDeleteId(null);
    };
    document.addEventListener("mousedown", handleMouseDown);
    return () => {
      window.clearTimeout(timer);
      document.removeEventListener("mousedown", handleMouseDown);
    };
  }, [armedDeleteId]);
  const [jumpAnchor, setJumpAnchor] = useState<HTMLElement | null>(null);
  const [jumpSearch, setJumpSearch] = useState("");
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [validationOpen, setValidationOpen] = useState(false);
  // Epic 80431: the version about to be test-run, while "Discard your unfinished test runs?" is open.
  const [testRunPrompt, setTestRunPrompt] = useState<{ runId: number; count: number } | null>(null);
  const [validationRows, setValidationRows] = useState<WorkflowValidationRow[]>([]);
  // The client checks ran but the save or server validate behind them failed.
  const [validationIncomplete, setValidationIncomplete] = useState(false);
  // Inline issues appear once Validate has run. Client issues then track the canvases live; the
  // server ones are for the graph it validated, so the next edit clears them.
  const [inlineIssuesShown, setInlineIssuesShown] = useState(false);
  const [serverIssues, setServerIssues] = useState<WorkflowValidationIssueDto[]>([]);
  const [validated, setValidated] = useState(false);
  // Edits since the last save. A ref, not state: Validate and Preview save these first and then act
  // in the same handler, and the leave-page guard is asked mid-navigation — neither may see a
  // value from before the save.
  const hasUnsavedChanges = useRef(false);
  const [announcement, setAnnouncement] = useState("");
  const sequence = useRef(canvases.length + 1);

  const trace = useWorkflowBuilderTrace();

  const handleGraphLoaded = useCallback((graph: WorkflowBuilderGraph) => {
    trace({
      objectType: "Workflow",
      objectId: graph.definition.id,
      objectName: `Opened ${graph.definition.name} (v${graph.definition.versionNo}) in the Workflow Builder`,
      action: "Opened",
    });
    // A (re)load replaces everything on screen, unsaved edits included (Reload after a conflict).
    hasUnsavedChanges.current = false;
    setEditingCanvasId(null);
    setInlineIssuesShown(false);
    setServerIssues([]);
    setWorkflowTitle(graph.definition.name);
    setCanvases(graph.canvases);
    setSelectedId(graph.canvases[0]?.id ?? "");
    // Mirrors the server's IsValidated: a draft validated in an earlier session can still publish.
    setValidated(graph.definition.isValidated);
  }, [trace]);

  const {
    definition,
    status: loadStatus,
    saving,
    validating,
    publishing,
    retry: retryLoad,
    save,
    validate,
    publish,
    updating,
    saveSettings,
    toggleArchive,
    saveAsNewVersion,
    lockedByAnotherUser,
    lockedMessage,
    seedOnlyMessage,
    staleConflict,
    reload,
  } = useWorkflowBuilderGraph({ definitionId, onLoaded: handleGraphLoaded });
  const isLoading = loadStatus === "loading";
  const loadFailed = loadStatus === "error";
  // Nothing to act on until the graph is in: the top-bar actions stay disabled, and the palette and
  // Jump-to stay hidden.
  const graphUnavailable = isLoading || loadFailed;
  // One lifecycle call at a time: each carries the rowVersionTag the previous one returns.
  const busy = saving || validating || publishing || updating;

  // Builder CanEdit (Epic 80427).
  const { permissions } = useMenu();
  const canEdit = permissions.some(
    (permission) => permission.moduleName === ModuleNames.WorkflowBuilder && permission.canEdit,
  );
  // View-only: no palette, tools, Save, Publish or settings; every input disabled. A lock held by
  // another user lands here too, with its own banner, and so does a seed-only workflow (the PIF,
  // Epic 80431), which keeps Preview / Run.
  const seedOnly = seedOnlyMessage !== null;
  const readOnly = !canEdit || lockedByAnotherUser || seedOnly;

  const editingCanvas =
    canvases.find((canvas) => canvas.id === editingCanvasId) ?? null;

  const jumpCanvases = useMemo(() => {
    const query = jumpSearch.trim().toLowerCase();
    return canvases.filter(
      (canvas) =>
        !query ||
        canvas.name.toLowerCase().includes(query) ||
        WORKFLOW_CANVAS_LABELS[canvas.type].toLowerCase().includes(query),
    );
  }, [canvases, jumpSearch]);

  // Any edit re-locks Publish (Validate greys) — mirroring 80372 clearing IsValidated on save.
  const markDirty = () => {
    setValidated(false);
    setServerIssues([]);
    hasUnsavedChanges.current = true;
  };

  const inlineIssues = useMemo<WorkflowInlineIssues>(
    () =>
      inlineIssuesShown
        ? withServerIssues(issuesByCanvas(canvases), serverIssues, canvases)
        : { canvas: {}, field: {} },
    [canvases, inlineIssuesShown, serverIssues],
  );

  const handleTitleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setWorkflowTitle(event.target.value);
    markDirty();
  };

  // Every way into a canvas editor — tile, Jump-to, palette add, connector insert — is traced.
  const showCanvasEditor = (canvas: WorkflowCanvas) => {
    setSelectedId(canvas.id);
    setCanvasSnapshot({ ...canvas });
    setEditingCanvasId(canvas.id);
    trace({
      objectType: "WorkflowCanvas",
      objectId: definition?.id ?? null,
      objectName: `Opened the ${canvas.name} (${WORKFLOW_CANVAS_LABELS[canvas.type]}) canvas editor`,
      action: "Opened",
    });
  };

  const openCanvas = (canvasId: string) => {
    const canvas = canvases.find((item) => item.id === canvasId);
    if (!canvas) return;
    showCanvasEditor(canvas);
  };

  const takenStageKeys = () => canvases.map((canvas) => canvas.stageKey).filter(Boolean);

  const handleAdd = (type: WorkflowCanvasType) => {
    const canvas = createWorkflowCanvas(type, sequence.current++, takenStageKeys());
    setCanvases((current) => {
      const finalizeIndex = current.findIndex((item) => item.type === "finalize");
      const insertAt = finalizeIndex >= 0 ? finalizeIndex : current.length;
      return [...current.slice(0, insertAt), canvas, ...current.slice(insertAt)];
    });
    showCanvasEditor(canvas);
    const addedMessage = `${WORKFLOW_CANVAS_LABELS[type]} canvas added`;
    setAnnouncement(addedMessage);
    showToaster(addedMessage, SEVERITIES.SUCCESS);
    markDirty();
  };

  const handleInsert = (type: WorkflowCanvasType, afterIndex: number) => {
    const canvas = createWorkflowCanvas(type, sequence.current++, takenStageKeys());
    setCanvases((current) => [
      ...current.slice(0, afterIndex + 1),
      canvas,
      ...current.slice(afterIndex + 1),
    ]);
    showCanvasEditor(canvas);
    const insertedMessage = `${WORKFLOW_CANVAS_LABELS[type]} inserted`;
    setAnnouncement(insertedMessage);
    showToaster(insertedMessage, SEVERITIES.SUCCESS);
    markDirty();
  };

  const announceReorder = () => {
    setAnnouncement(REORDERED_MESSAGE);
    showToaster(REORDERED_MESSAGE, SEVERITIES.SUCCESS);
  };

  const handleMove = (canvasId: string, direction: "up" | "down") => {
    setCanvases((current) => {
      const from = current.findIndex((canvas) => canvas.id === canvasId);
      const to = direction === "up" ? from - 1 : from + 1;
      if (from < 0 || to < 0 || to >= current.length) return current;
      const reordered = [...current];
      [reordered[from], reordered[to]] = [reordered[to], reordered[from]];
      return reordered;
    });
    announceReorder();
    markDirty();
  };

  const handleDropReorder = (sourceId: string, targetId: string) => {
    if (sourceId === targetId) return;
    setCanvases((current) => {
      const sourceIndex = current.findIndex((canvas) => canvas.id === sourceId);
      const targetIndex = current.findIndex((canvas) => canvas.id === targetId);
      if (sourceIndex < 0 || targetIndex < 0) return current;
      const reordered = [...current];
      const [moved] = reordered.splice(sourceIndex, 1);
      // The dropped tile takes the target's place: after it when dragged down, before it when
      // dragged up (inserting at the target's ORIGINAL index does both, since removing a tile from
      // above shifts the target up by one). Never past Finalize - only a Cancel may sit after it.
      const finalizeIndex = reordered.findIndex((canvas) => canvas.type === "finalize");
      const position =
        finalizeIndex >= 0 && moved.type !== "cancel" ? Math.min(targetIndex, finalizeIndex) : targetIndex;
      reordered.splice(position, 0, moved);
      return reordered;
    });
    announceReorder();
    markDirty();
  };

  const handleArmDelete = (canvasId: string) => setArmedDeleteId(canvasId);

  const handleDelete = (canvasId: string) => {
    // The flow is never empty: the last canvas can't go (from the tile or the canvas editor).
    if (canvases.length <= 1) {
      setArmedDeleteId(null);
      setAnnouncement(LAST_CANVAS_MESSAGE);
      showToaster(LAST_CANVAS_MESSAGE, SEVERITIES.INFO);
      return;
    }
    const removedIndex = canvases.findIndex((canvas) => canvas.id === canvasId);
    const remaining = canvases.filter((canvas) => canvas.id !== canvasId);
    setCanvases(remaining);
    if (selectedId === canvasId) {
      setSelectedId(remaining[Math.max(0, removedIndex - 1)]?.id ?? "");
    }
    setArmedDeleteId(null);
    setEditingCanvasId(null);
    setAnnouncement("Canvas removed");
    markDirty();
  };

  const handleLiveCanvasChange = (changedCanvas: WorkflowCanvas) => {
    setCanvases((current) =>
      current.map((canvas) => (canvas.id === changedCanvas.id ? changedCanvas : canvas)),
    );
    markDirty();
  };

  const handleCanvasSave = () => {
    setEditingCanvasId(null);
    setCanvasSnapshot(null);
    setAnnouncement("Canvas changes saved");
  };

  const handleCanvasDiscard = () => {
    if (canvasSnapshot) {
      setCanvases((current) =>
        current.map((canvas) => (canvas.id === canvasSnapshot.id ? canvasSnapshot : canvas)),
      );
    }
    setEditingCanvasId(null);
    setCanvasSnapshot(null);
  };

  const closeJumpMenu = () => {
    setJumpAnchor(null);
    setJumpSearch("");
  };

  const handleJumpSelection = (canvasId: string) => {
    closeJumpMenu();
    openCanvas(canvasId);
  };

  const handleJumpSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    setJumpSearch(event.target.value);
  };

  // One whole-graph save. The canvases are re-keyed onto the ids the server assigned (so the next
  // save updates rather than re-inserts them) through a functional update, so a canvas added while
  // the request was in flight is kept as-is. `quiet` skips the toast when Validate or Preview save
  // first. Resolves the saved definition id, or null when nothing was saved.
  const persistDraft = async (quiet = false): Promise<number | null> => {
    // Only a draft can be saved; the server answers anything else with a 409 that would read as
    // "changed since you opened it". Editing a published version means forking a new one.
    if (definition && definition.status !== "draft") {
      showToaster(nonDraftSaveMessage(definition.status), SEVERITIES.INFO);
      return null;
    }
    const saved = await save(workflowTitle.trim() || NEW_WORKFLOW.name, canvases);
    if (!saved) return null;

    const { request, result, created } = saved;
    const { stageIdRenames } = applySaveResult([], request, result);
    const renamed = (id: string) => stageIdRenames[id] ?? id;
    setCanvases((current) => applySaveResult(current, request, result).canvases);
    setSelectedId(renamed);
    setEditingCanvasId((id) => (id ? renamed(id) : id));
    setValidated(false);
    hasUnsavedChanges.current = false;
    if (!quiet) {
      setAnnouncement("Draft saved");
      showToaster("Draft saved", SEVERITIES.SUCCESS);
    }

    // A new draft now has an id: put it in the URL so a reload reopens it rather than a blank one.
    if (created) {
      navigate(`/workflow/builder?definitionId=${result.definitionId}`, { replace: true });
    }
    return result.definitionId;
  };

  const handleSaveDraft = () => {
    void persistDraft();
  };

  // ----- the wrench -----

  const handleSettingsClose = () => setSettingsOpen(false);

  const handleSettingsSave = async (values: WorkflowSettingsValues) => {
    // Settings need a definition to attach to; a never-saved workflow is saved first.
    if (!definition && !(await persistDraft(true))) return;
    if (!(await saveSettings(values))) return;
    // Not markDirty: settings don't touch the graph or the validated flag.
    setWorkflowTitle(values.name);
    setSettingsOpen(false);
    setAnnouncement(WORKFLOW_SETTINGS_MESSAGES.SAVED);
    showToaster(WORKFLOW_SETTINGS_MESSAGES.SAVED, SEVERITIES.SUCCESS);
  };

  const handleToggleArchive = async () => {
    const status = await toggleArchive();
    if (!status) return;
    // An archived row can't publish, and a restored one is a draft to validate again.
    setValidated(false);
    const message =
      status === "archived"
        ? WORKFLOW_SETTINGS_MESSAGES.ARCHIVED
        : WORKFLOW_SETTINGS_MESSAGES.UNARCHIVED;
    setAnnouncement(message);
    showToaster(message, SEVERITIES.SUCCESS);
  };

  const handleSaveAsNewVersion = async () => {
    const created = await saveAsNewVersion(
      workflowTitle.trim() || NEW_WORKFLOW.name,
      canvases,
      hasUnsavedChanges.current,
    );
    if (!created) return;

    setSettingsOpen(false);
    hasUnsavedChanges.current = false;
    const message = `Saved as v${created.versionNo} (new draft)`;
    setAnnouncement(message);
    showToaster(message, SEVERITIES.SUCCESS);
    if (!created.editsCarried) {
      showToaster(WORKFLOW_SETTINGS_MESSAGES.EDITS_NOT_CARRIED, SEVERITIES.WARNING);
    }
    // Opens the new draft; the published version it was copied from is left untouched.
    navigate(`/workflow/builder?definitionId=${created.definitionId}`);
  };

  const handleSettingsSaveClick = (values: WorkflowSettingsValues) => {
    void handleSettingsSave(values);
  };

  const handleToggleArchiveClick = () => {
    void handleToggleArchive();
  };

  const handleSaveAsNewVersionClick = () => {
    void handleSaveAsNewVersion();
  };

  const handleValidationClose = () => setValidationOpen(false);

  const handleValidateClick = () => {
    void handleValidate();
  };

  const handlePublishClick = () => {
    void handlePublish();
  };

  const reportValidation = (rows: WorkflowValidationRow[]) => {
    setValidationRows(rows);
    const passed = allRowsPassed(rows);
    setValidated(passed);
    const message = passed
      ? WORKFLOW_VALIDATION_MESSAGES.ALL_PASSED
      : WORKFLOW_VALIDATION_MESSAGES.NEEDS_ATTENTION;
    setAnnouncement(message);
    showToaster(message, passed ? SEVERITIES.SUCCESS : SEVERITIES.WARNING);
  };

  // The client checks answer instantly. Only a clean pass goes to the server, which checks the
  // saved draft (and adds the start / reachability / dangling-route checks) and sets IsValidated.
  const handleValidate = async () => {
    const clientRows = runClientValidation(canvases);
    setValidationRows(clientRows);
    setValidationIncomplete(false);
    setServerIssues([]);
    setInlineIssuesShown(true);
    setValidationOpen(true);
    // View-only: the checklist can be read, but the server half saves and is a CanEdit call.
    if (readOnly || !allRowsPassed(clientRows)) {
      reportValidation(clientRows);
      return;
    }

    // A failed save or validate has already been reported; the checklist must not then read as a
    // pass just because the client rules alone passed.
    const markIncomplete = () => {
      setValidated(false);
      setValidationIncomplete(true);
    };
    if (hasUnsavedChanges.current || !definition) {
      const saved = await persistDraft(true);
      if (!saved) {
        markIncomplete();
        return;
      }
    }
    const result = await validate();
    if (!result) {
      markIncomplete();
      return;
    }
    setServerIssues(result.issues);
    const rows = mergeServerIssues(clientRows, result.issues);
    if (!result.isValidated && allRowsPassed(rows)) {
      // The server refused without an issue any row covers — never let that read as green.
      rows.push({
        id: "server-not-validated",
        label: "The server did not validate this draft. Save and validate again.",
        passed: false,
        messages: [],
      });
    }
    reportValidation(rows);
  };

  const handlePublish = async () => {
    if (!validated) return;
    const outcome = await publish();
    if (!outcome) return;

    if (outcome.kind === "stale") {
      // The banner offers Reload; a stale draft can't be published as it stands.
      setValidated(false);
      return;
    }
    if (outcome.kind === "blocked") {
      setValidated(false);
      setValidationRows(mergeServerIssues(runClientValidation(canvases), outcome.issues));
      setServerIssues(outcome.issues);
      setInlineIssuesShown(true);
      setValidationOpen(true);
      showToaster(WORKFLOW_VALIDATION_MESSAGES.PUBLISH_BLOCKED, SEVERITIES.ERROR);
      return;
    }
    // A published version is done; editing it further starts a new version, so Publish re-locks.
    setValidated(false);
    setAnnouncement("Workflow published");
    showToaster("Workflow published", SEVERITIES.SUCCESS);
  };

  // Preview / Run auto-saves first, so the Run screen runs exactly what is on screen — there is
  // no "which version am I testing?" ambiguity.
  const handlePreview = async () => {
    const runId =
      hasUnsavedChanges.current || !definition ? await persistDraft(true) : definition.id;
    if (!runId) return;
    trace({
      objectType: "Workflow",
      objectId: runId,
      objectName: `Switched ${workflowTitle.trim() || NEW_WORKFLOW.name} to Preview / Run`,
      action: "Navigated",
    });
    // Epic 80431: open test runs of this workflow are offered for discard first. A failed count must
    // not stop anyone testing, so it simply skips the question.
    let openTestRuns = 0;
    try {
      openTestRuns = await getWorkflowOpenTestRuns(runId);
    } catch {
      openTestRuns = 0;
    }
    if (openTestRuns > 0) {
      setTestRunPrompt({ runId, count: openTestRuns });
      return;
    }
    // `source=builder` tells the runtime it was opened from here, so it offers the Builder /
    // Preview toggle instead of Back to Library.
    navigate(testRunUrl(runId));
  };

  // Yes discards them in the same call that starts the new run; No keeps them; the close button
  // cancels the run altogether.
  const handleTestRunPrompt = (discard: boolean) => {
    const prompt = testRunPrompt;
    setTestRunPrompt(null);
    // A destructive choice rides in navigation state, not a URL someone could refresh or share.
    if (prompt) {
      if (discard) navigate(testRunUrl(prompt.runId), { state: { discardOpenTestRuns: true } });
      else navigate(testRunUrl(prompt.runId));
    }
  };

  const handlePreviewClick = () => {
    void handlePreview();
  };

  const leaveGuard = useUnsavedChangesGuard(() => !readOnly && hasUnsavedChanges.current);

  const handleLeavePrompt = (leave: boolean) => (leave ? leaveGuard.proceed() : leaveGuard.stay());

  return (
    <main className="workflow-builder">
      <PageBand
        title="Workflow Builder"
        breadcrumbs={
          <Breadcrumbs
            aria-label="breadcrumbs"
            sx={{ "& .MuiBreadcrumbs-separator": { color: BREADCRUMB_LINK_COLOR } }}
          >
            <Link
              component={RouterLink}
              to="/workflow/library"
              underline="hover"
              sx={{ color: BREADCRUMB_LINK_COLOR }}
            >
              Workflow Management
            </Link>
            <Typography sx={{ color: BREADCRUMB_LINK_COLOR }}>Builder</Typography>
            <Typography sx={{ fontWeight: 600, color: "#fff" }}>{workflowTitle}</Typography>
          </Breadcrumbs>
        }
      />

      <section className="workflow-builder__editor" aria-label="Workflow Builder">
        <header className="workflow-builder__topbar">
          {isLoading || loadFailed ? (
            <Box className="workflow-builder__title-wrap" aria-hidden={isLoading}>
              {isLoading && (
                <>
                  <Skeleton variant="text" width={280} height={32} />
                  <Skeleton variant="text" width={180} />
                </>
              )}
            </Box>
          ) : (
            <Box className="workflow-builder__title-wrap">
              <TextField
                className="workflow-builder__title-input"
                value={workflowTitle}
                onChange={handleTitleChange}
                inputProps={{ "aria-label": "Workflow title" }}
                variant="outlined"
                size="small"
                disabled={readOnly}
              />
              <Box className="workflow-builder__meta">
                <span className="workflow-builder__version">
                  v{definition?.versionNo ?? NEW_WORKFLOW.version}
                </span>
                <Chip
                  className={`workflow-builder__status workflow-builder__status--${
                    definition?.status ?? NEW_WORKFLOW.status
                  }`}
                  label={
                    <>
                      <span className="workflow-builder__status-dot" aria-hidden="true" />
                      {definition?.status ?? NEW_WORKFLOW.status}
                    </>
                  }
                  size="small"
                />
                <span>·</span>
                <span>{definition?.category ?? NEW_WORKFLOW.category}</span>
              </Box>
            </Box>
          )}

          <Box className="workflow-builder__actions">
            {!graphUnavailable && (
              <Button
                variant="outlined"
                startIcon={<AltRouteOutlined />}
                onClick={(event) => setJumpAnchor(event.currentTarget)}
                aria-haspopup="menu"
                aria-expanded={Boolean(jumpAnchor)}
              >
                Go to
              </Button>
            )}
            {!readOnly && (
              /* A disabled button fires no pointer events, so the Tooltip listens on a span. */
              <Tooltip title="Workflow settings" placement="top">
                <span className="d-inline-flex">
                  <IconButton
                    className="workflow-builder__settings-button"
                    onClick={() => setSettingsOpen(true)}
                    aria-label="Workflow settings"
                    disabled={graphUnavailable}
                  >
                    <BuildOutlined />
                  </IconButton>
                </span>
              </Tooltip>
            )}
            {/* A seed-only workflow (the PIF) can still be test-run: it has no unsaved edits, so
                Preview / Run opens it without saving. */}
            {(!readOnly || (seedOnly && canEdit && !lockedByAnotherUser)) && (
              <>
                <Box className="workflow-builder__mode" role="group" aria-label="Editor mode">
                  <Button
                    className="workflow-builder__mode-active"
                    startIcon={<AccountTreeOutlined />}
                    aria-pressed
                  >
                    Builder
                  </Button>
                  <Button
                    startIcon={<PlayArrowOutlined />}
                    onClick={handlePreviewClick}
                    disabled={graphUnavailable}
                  >
                    Preview / Run
                  </Button>
                </Box>
              </>
            )}
            <Button
              className={validated ? "workflow-builder__validated" : ""}
              variant="outlined"
              startIcon={
                validating ? <CircularProgress size={16} color="inherit" /> : <CheckCircleOutline />
              }
              onClick={handleValidateClick}
              disabled={graphUnavailable || busy}
            >
              {validated ? "Validated" : "Validate"}
            </Button>
            {!readOnly && (
              <>
                <Button
                  className="workflow-builder__save"
                  variant="outlined"
                  startIcon={
                    saving ? <CircularProgress size={16} color="inherit" /> : <SaveOutlined />
                  }
                  onClick={handleSaveDraft}
                  disabled={graphUnavailable || busy}
                >
                  Save draft
                </Button>
                {/* A disabled button fires no pointer events, so its Tooltip listens on the span. */}
                <Tooltip
                  title={validated ? "Publish workflow" : WORKFLOW_VALIDATION_MESSAGES.VALIDATE_FIRST}
                >
                  <span className="d-inline-flex">
                    <Button
                      className="workflow-builder__publish"
                      variant="contained"
                      startIcon={
                        publishing ? <CircularProgress size={16} color="inherit" /> : <PublishOutlined />
                      }
                      disabled={!validated || graphUnavailable || busy}
                      onClick={handlePublishClick}
                    >
                      Publish
                    </Button>
                  </span>
                </Tooltip>
              </>
            )}
          </Box>
        </header>

        {staleConflict && !graphUnavailable && (
          <Alert
            severity="warning"
            className="workflow-builder__read-only"
            action={
              <Button color="inherit" size="small" onClick={reload}>
                Reload
              </Button>
            }
          >
            {WORKFLOW_SAVE_MESSAGES.STALE}
          </Alert>
        )}
        {readOnly && !graphUnavailable && (
          <Alert severity="info" className="workflow-builder__read-only">
            {lockedByAnotherUser ? lockedMessage : (seedOnlyMessage ?? READ_ONLY_MESSAGE)}
          </Alert>
        )}

        {isLoading ? (
          <Box className="workflow-builder__loading" aria-busy="true" aria-label="Loading workflow">
            {[0, 1, 2].map((index) => (
              <Skeleton key={index} variant="rounded" height={96} />
            ))}
          </Box>
        ) : loadFailed ? (
          <Box className="workflow-builder__load-error">
            <Alert
              severity="error"
              action={
                <Button color="inherit" size="small" onClick={retryLoad}>
                  Retry
                </Button>
              }
            >
              {LOAD_ERROR_MESSAGE}
            </Alert>
          </Box>
        ) : (
          <Box
            className={`workflow-builder__body${readOnly ? " workflow-builder__body--view-only" : ""}`}
          >
            {!readOnly && <WorkflowCanvasPalette onAdd={handleAdd} />}
            <WorkflowReadOnlyProvider value={readOnly}>
              <WorkflowFlow
                canvases={canvases}
                selectedId={selectedId}
                armedDeleteId={armedDeleteId}
                onSelect={openCanvas}
                onMove={handleMove}
                onDelete={handleDelete}
                onArmDelete={handleArmDelete}
                onInsert={handleInsert}
                onDropReorder={handleDropReorder}
                issues={inlineIssues.canvas}
              />
            </WorkflowReadOnlyProvider>
          </Box>
        )}
      </section>

      <WorkflowReadOnlyProvider value={readOnly}>
        <WorkflowCanvasDialog
          canvas={editingCanvas}
          canvases={canvases}
          open={Boolean(editingCanvas)}
          onLiveChange={handleLiveCanvasChange}
          onSave={handleCanvasSave}
          onDiscard={handleCanvasDiscard}
          onDelete={handleDelete}
          canvasIssues={editingCanvas ? inlineIssues.canvas[editingCanvas.id] : undefined}
          fieldIssues={inlineIssues.field}
        />
      </WorkflowReadOnlyProvider>

      {/* A dropdown anchored under its button, as in the prototype's `.jump-menu` — not a modal.
          `autoFocus={false}` leaves the focus for the search box below. */}
      <Menu
        anchorEl={jumpAnchor}
        open={Boolean(jumpAnchor)}
        onClose={closeJumpMenu}
        // Opting the menu list out of MUI's own focus grab, not adding autofocus — the search box
        // below takes it instead, so the rule's concern doesn't apply.
        // eslint-disable-next-line jsx-a11y/no-autofocus
        autoFocus={false}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        className="workflow-builder__jump-menu"
        MenuListProps={{ "aria-label": "Go to a canvas", dense: true }}
      >
        <Box className="workflow-builder__jump-search">
          <TextField
            fullWidth
            size="small"
            // Focus has to land somewhere inside an opened menu; the prototype focuses this box
            // (`si.focus()`), which also keeps the first keystroke out of MUI's type-ahead.
            // eslint-disable-next-line jsx-a11y/no-autofocus
            autoFocus
            placeholder="Search canvases…"
            inputProps={{ "aria-label": "Search canvases" }}
            value={jumpSearch}
            onChange={handleJumpSearch}
            // Typing inside a Menu otherwise jumps focus to whichever item starts with that letter.
            onKeyDown={(event) => event.stopPropagation()}
            InputProps={{ startAdornment: <Search /> }}
          />
        </Box>
        {jumpCanvases.map((canvas) => (
          <MenuItem key={canvas.id} onClick={() => handleJumpSelection(canvas.id)}>
            <span
              className={`workflow-builder__menu-icon workflow-builder__canvas-icon--${canvas.type}`}
            >
              <WorkflowCanvasIcon type={canvas.type} />
            </span>
            <span className="workflow-builder__jump-copy">
              <strong>{canvas.name}</strong>
              <span>{WORKFLOW_CANVAS_LABELS[canvas.type]}</span>
            </span>
          </MenuItem>
        ))}
        {jumpCanvases.length === 0 && (
          <Typography className="workflow-builder__no-results">No canvases match</Typography>
        )}
      </Menu>

      <WorkflowSettingsDialog
        // Remounted per opening so the form starts from the saved settings each time.
        key={settingsOpen ? "settings-open" : "settings-closed"}
        open={settingsOpen}
        definition={definition}
        title={workflowTitle}
        busy={updating || saving}
        onClose={handleSettingsClose}
        onSave={handleSettingsSaveClick}
        onToggleArchive={handleToggleArchiveClick}
        onSaveAsNewVersion={handleSaveAsNewVersionClick}
      />

      <WorkflowValidationDialog
        open={validationOpen}
        rows={validationRows}
        running={saving || validating}
        incomplete={validationIncomplete}
        onClose={handleValidationClose}
      />

      <ConfirmationDialog
        open={leaveGuard.blocked}
        title="Unsaved changes"
        message={UNSAVED_CHANGES_MESSAGE}
        onClose={handleLeavePrompt}
        // Leaving throws the edits away, so Enter on open must not be the destructive answer.
        initialFocus="no"
      />

      <ConfirmationDialog
        open={testRunPrompt !== null}
        title={UNFINISHED_TEST_RUNS_TITLE}
        message={unfinishedTestRunsMessage(testRunPrompt?.count ?? 0)}
        onClose={handleTestRunPrompt}
        onDiscard={() => setTestRunPrompt(null)}
        // Discarding ends the old runs, so Enter on open must not be the destructive answer.
        initialFocus="no"
      />

      <Typography className="workflow-builder__announcement" role="status" aria-live="polite">
        {announcement}
      </Typography>
    </main>
  );
};

/**
 * The route element. Opening the Builder with no definition (the side-nav "Builder" link) while it
 * already holds one stays on the same route, so React keeps the page and every piece of its state -
 * the held definition, its status and keys included. A new workflow then still carried the old one,
 * and after a publish every Save draft / Preview answered "This version is published". Losing the
 * definitionId therefore remounts the page on the empty skeleton. Gaining one does not: a new
 * draft's first save puts its id into the URL, and must keep what is on screen.
 */
const WorkflowBuilderRoute: React.FC = () => {
  const [searchParams] = useSearchParams();
  const definitionId = Number(searchParams.get("definitionId")) || 0;
  const [mount, setMount] = useState({ definitionId, key: 0 });

  // Adjusted while rendering (React's derived-state pattern), so the stale page never renders
  // against the new URL.
  if (mount.definitionId !== definitionId) {
    setMount({ definitionId, key: mount.definitionId && !definitionId ? mount.key + 1 : mount.key });
  }

  return <WorkflowBuilder key={mount.key} />;
};

export default WorkflowBuilderRoute;
